import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RoleConfirmationStep = ({ setupData, setSetupData, onComplete, onBack, isLoading }) => {
  const [errors, setErrors] = useState({});

  const handleCheckboxChange = (field, value) => {
    setSetupData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!setupData?.roleAccepted) {
      newErrors.roleAccepted = 'You must acknowledge your role and permissions';
    }

    if (!setupData?.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms of service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleComplete = () => {
    if (validateForm()) {
      onComplete();
    }
  };

  const permissions = [
    {
      icon: 'FolderPlus',
      title: 'Gallery Creation',
      description: 'Create and manage galleries with media content',
      included: true
    },
    {
      icon: 'HardDrive',
      title: 'Google Drive Integration',
      description: 'Connect and sync media from Google Drive folders',
      included: true
    },
    {
      icon: 'Users',
      title: 'Client Management',
      description: 'Share galleries with clients and track visitor analytics',
      included: true
    },
    {
      icon: 'BarChart3',
      title: 'Analytics Access',
      description: 'View gallery performance and visitor engagement metrics',
      included: true
    },
    {
      icon: 'Settings',
      title: 'Gallery Settings',
      description: 'Configure privacy settings and access controls',
      included: true
    },
    {
      icon: 'UserCog',
      title: 'Owner Privileges',
      description: 'Manage contributors and system settings',
      included: false
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Confirm Your Role</h2>
        <p className="text-muted-foreground">
          Review your permissions as a contributor
        </p>
      </div>

      {/* Role Badge */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <Icon name="Shield" size={24} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Contributor</h3>
          <p className="text-sm text-muted-foreground">Gallery management and client collaboration</p>
        </div>
      </div>

      {/* Permissions */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Your Permissions</h3>
        <div className="space-y-2">
          {permissions?.map((permission, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                permission?.included
                  ? 'bg-muted/30 border-border' :'bg-muted/10 border-muted opacity-60'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                permission?.included ? 'bg-primary/10' : 'bg-muted'
              }`}>
                <Icon
                  name={permission?.icon}
                  size={16}
                  className={permission?.included ? 'text-primary' : 'text-muted-foreground'}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{permission?.title}</p>
                  {permission?.included ? (
                    <Icon name="Check" size={14} className="text-success" />
                  ) : (
                    <Icon name="X" size={14} className="text-muted-foreground" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{permission?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Acknowledgments */}
      <div className="space-y-4 pt-2">
        <div className="space-y-3">
          <Checkbox
            id="roleAccepted"
            checked={setupData?.roleAccepted}
            onCheckedChange={(checked) => handleCheckboxChange('roleAccepted', checked)}
            label="I understand my role and permissions as a contributor"
            description="You acknowledge the capabilities and limitations of your contributor account"
            error={errors?.roleAccepted}
          />
          <Checkbox
            id="termsAccepted"
            checked={setupData?.termsAccepted}
            onCheckedChange={(checked) => handleCheckboxChange('termsAccepted', checked)}
            label="I accept the Terms of Service and Privacy Policy"
            description="You agree to follow platform guidelines and data protection policies"
            error={errors?.termsAccepted}
          />
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
        <Icon name="Info" size={18} className="text-primary mt-0.5 flex-shrink-0" />
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground mb-1">Ready to Get Started?</p>
          <p>
            Once you complete setup, you'll be redirected to your contributor dashboard where you can
            start creating galleries and managing media content.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={onBack}
          variant="outline"
          size="lg"
          className="flex-1"
          iconName="ArrowLeft"
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          onClick={handleComplete}
          variant="default"
          size="lg"
          className="flex-1"
          iconName="Check"
          iconPosition="right"
          loading={isLoading}
        >
          Complete Setup
        </Button>
      </div>
    </div>
  );
};

export default RoleConfirmationStep;